"use client";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ImageWrapper from "../ImageWrapper";
import CustomInput from "../CustomInput";
import { LeadFormData } from "@/app/utilities/interfaces";
import { sanitizePhoneInput, validateEmail, validateName, validatePhoneNumber } from "@/app/utilities/formValidation";
import { detectLocation } from "@/app/utilities/apis";
import { getCookie } from "@/app/utilities/storage";
import LocationMasterSearchInput from "../LocationMasterSearchInput";
import { mapLocationToLeadFields } from "@/app/utilities/locationUtils";
import { getLastLeadFormData, storeLastLeadFormData } from "@/utils/leadTracking";
import { MODAL_TRACKING } from "@/lib/constants/tracking";
import { useLeadConsent } from "@/utils/hooks/useLeadConsent";
import LeadConsent from "./LeadConsent";

const hasSelectedLocationData = (locationData?: Record<string, any>) => {
    if (!locationData) return false;
    return Object.values(locationData).some((value) => value !== undefined && value !== null && value !== "");
};

const MOBILE_LEAD_UA = /Android|iPhone|iPad|iPod|Mobile/i;
const MOBILE_LEAD_TEXT_INPUT =
    'input:not([type="hidden"]):not([disabled]):not([readonly]), textarea:not([disabled]):not([readonly])';

let mobileLeadKeyboardFromUserGesture = false;
const MOBILE_KEYBOARD_PRIME_ID = "cj-mobile-keyboard-prime";

function primeMobileKeyboardInUserGesture() {
    if (typeof document === "undefined" || !isMobileLeadViewport()) return;

    let prime = document.getElementById(MOBILE_KEYBOARD_PRIME_ID) as HTMLInputElement | null;
    if (!prime) {
        prime = document.createElement("input");
        prime.id = MOBILE_KEYBOARD_PRIME_ID;
        prime.type = "text";
        prime.setAttribute("autocomplete", "off");
        prime.setAttribute("aria-hidden", "true");
        prime.setAttribute("tabindex", "-1");
        Object.assign(prime.style, {
            position: "fixed",
            top: "0",
            left: "0",
            width: "1px",
            height: "1px",
            opacity: "0",
            fontSize: "16px",
            pointerEvents: "none",
        });
        document.body.appendChild(prime);
    }

    prime.readOnly = false;
    focusInputForMobileKeyboard(prime);
}

function dismissMobileKeyboardPrime() {
    const prime = document.getElementById(MOBILE_KEYBOARD_PRIME_ID);
    prime?.remove();
}

/** Call from the tap/click handler that opens a lead popup (keeps iOS/Android keyboard allowed). */
export function requestMobileLeadKeyboardForNextForm() {
    mobileLeadKeyboardFromUserGesture = true;
    primeMobileKeyboardInUserGesture();
}

export function consumeMobileLeadKeyboardRequest() {
    const fromGesture = mobileLeadKeyboardFromUserGesture;
    mobileLeadKeyboardFromUserGesture = false;
    return fromGesture;
}

function isMobileLeadViewport() {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches || MOBILE_LEAD_UA.test(navigator.userAgent);
}

function focusInputForMobileKeyboard(input: HTMLInputElement | HTMLTextAreaElement) {
    input.focus({ preventScroll: false });
    // iOS often blocks the keyboard unless focus happens in the user-gesture window; this helps when it does not.
    const previousReadOnly = input.readOnly;
    input.readOnly = true;
    input.focus({ preventScroll: false });
    input.readOnly = previousReadOnly;
    input.focus({ preventScroll: false });
    try {
        const len = input.value.length;
        input.setSelectionRange(len, len);
    } catch {
        /* unsupported input type */
    }
}

/** Used by lead popups on mobile: focus first empty text field so the keyboard can open. */
export function focusFirstEmptyLeadFieldOnMobile(container: HTMLElement | null) {
    if (!container || !isMobileLeadViewport()) return;

    const fields = Array.from(container.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(MOBILE_LEAD_TEXT_INPUT));
    if (!fields.length) return;

    const target = fields.find((el) => !el.value.trim()) ?? fields[0];
    focusInputForMobileKeyboard(target);
    requestAnimationFrame(() => dismissMobileKeyboardPrime());
}

type OnSubmitResult = boolean | { success?: boolean; payload?: any } | void;

interface Props {
    initialData?: Partial<LeadFormData>;
    submitLabel?: string;
    onSubmit: (data: LeadFormData) => Promise<OnSubmitResult> | OnSubmitResult;
    onError?: (msg: string) => void;
    disabled?: boolean;
    className?: string;
    LeadPopupSubmitTracking?: string;
    trackingClass?: string;
    leadsumbittracking?: string;
    // Renders a mandatory email field — only for models with email_required
    showEmail?: boolean;
    /** Splash popup: no auto-focus / keyboard on open */
    disableMobileAutofocus?: boolean;
}

const LeadForm: React.FC<Props> = ({
    initialData = {},
    submitLabel = "Submit",
    onSubmit,
    onError,
    disabled = false,
    className = "",
    LeadPopupSubmitTracking,
    trackingClass,
    leadsumbittracking,
    showEmail = false,
    disableMobileAutofocus = false,
}) => {
    const [leadData, setLeadData] = useState<LeadFormData>({ name: "", phone: "", location: "", locationData: {}, ...initialData });
    const [phoneError, setPhoneError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);
    const [locationError, setLocationError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const consent = useLeadConsent();
    const formRef = useRef<HTMLDivElement>(null);
    const didAutofocusRef = useRef(false);

    // Populate defaults (user cookie, location, UTM) on mount — parents need not provide these
    useEffect(() => {
        const populateDefaults = () => {
            const { name: userName, mobile } = (() => {
                const userMatch = document.cookie.match(/(?:^|; )user=([^;]*)/);
                if (!userMatch?.[1]) return { name: '', mobile: '' };
                try { const user = JSON.parse(decodeURIComponent(userMatch[1])); return { name: user?.user?.name || '', mobile: user?.user?.mobile || '' }; } catch { return { name: '', mobile: '' }; }
            })();

            const utmKeys = ['utm_campaign', 'utm_medium', 'utm_term', 'utm_source', 'utm_content'];
            const utms = utmKeys.reduce<Record<string, string | undefined>>((acc, k) => { acc[k] = getCookie(k); return acc; }, {});

            // Prefetch from a lead already submitted earlier this session (any
            // vehicle) so the user isn't asked to retype name/phone/location.
            const lastLead = getLastLeadFormData();

            setLeadData(prev => ({
                ...prev,
                name: lastLead?.name || userName,
                phone: lastLead?.phone || mobile,
                location: lastLead?.location || "",
                locationData: lastLead?.locationData || {},
                ...utms,
                ...initialData,
            }));
        };

        populateDefaults();
        // Run once on mount — avoid re-running when parent recreates `initialData` object
    }, []);

    useLayoutEffect(() => {
        if (disableMobileAutofocus) {
            consumeMobileLeadKeyboardRequest();
            dismissMobileKeyboardPrime();
            return;
        }
        if (didAutofocusRef.current) return;
        didAutofocusRef.current = true;
        const fromUserGesture = consumeMobileLeadKeyboardRequest();
        const runFocus = () => focusFirstEmptyLeadFieldOnMobile(formRef.current);

        if (fromUserGesture) {
            runFocus();
            requestAnimationFrame(runFocus);
            window.setTimeout(runFocus, 50);
            return;
        }

        const timer = window.setTimeout(runFocus, 220);
        return () => window.clearTimeout(timer);
    }, [disableMobileAutofocus]);

    // Handlers for input changes with validation
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = sanitizePhoneInput(e.target.value).slice(0, 10);
        setLeadData(prev => ({ ...prev, phone: value }));
        setPhoneError(validatePhoneNumber(value));
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/\s/g, "").slice(0, 254);
        setLeadData(prev => ({ ...prev, email: value }));
        setEmailError(validateEmail(value));
    };

    // Only allow alphabets and spaces for name
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^A-Za-z\s]/g, "");
        setLeadData(prev => ({ ...prev, name: value }));
        setNameError(validateName(value));
    };

    // Allow alphabets, numbers (for pincode), spaces, and commas for location
    const handleLocationChange = (value: string, selectedLocation?: any) => {
        // Allow letters, numbers, spaces, and commas - to support pincodes
        let sanitizedValue = value.replace(/[^A-Za-z0-9\s,]/g, "");
        if (sanitizedValue.startsWith(",")) sanitizedValue = sanitizedValue.slice(1);
        // Map selected location breakdown into lead payload when available
        const mapped = selectedLocation ? mapLocationToLeadFields(selectedLocation) : { locationData: {} };
        const locationData = mapped.locationData || {};
        const isSelected = hasSelectedLocationData(locationData);

        setLeadData(prev => ({ ...prev, location: sanitizedValue, locationData }));
        if (!sanitizedValue.trim()) setLocationError("Location is required");
        else if (sanitizedValue.trim().length < 2) setLocationError("Location must be at least 2 characters");
        else if (!isSelected) setLocationError("Please select a location from suggestions");
        else setLocationError(null);
    };

    // Form validation before submission
    const validateForm = (): boolean => {
        let hasError = false;
        const nameErr = leadData.name.trim() ? validateName(leadData.name) : "Please enter your name.";
        if (nameErr) { setNameError(nameErr); hasError = true; }

        if (!leadData.phone.trim()) {
            setPhoneError("Please enter your phone number");
            hasError = true;
        } else if (leadData.phone.length !== 10) {
            setPhoneError("Please enter a 10-digit phone number");
            hasError = true;
        } else {
            const phoneErr = validatePhoneNumber(leadData.phone);
            if (phoneErr) { setPhoneError(phoneErr); hasError = true; }
        }

        if (showEmail) {
            const emailErr = validateEmail(leadData.email || "");
            if (emailErr) { setEmailError(emailErr); hasError = true; }
        }

        if (!(leadData.location || "").trim()) {
            setLocationError("Please enter your location.");
            hasError = true;
        } else if (!hasSelectedLocationData(leadData.locationData as Record<string, any>)) {
            setLocationError("Please select a location from suggestions");
            hasError = true;
        }

        return !hasError;
    };

    // Submit handler that calls parent onSubmit and manages local form state based on result
    const handleSubmit = async () => {
        if (!consent.canSubmit || !validateForm()) return;
        setIsSubmitting(true);
        try {
            storeLastLeadFormData({
                name: leadData.name,
                phone: leadData.phone,
                location: leadData.location,
                locationData: leadData.locationData as Record<string, any>,
            });
            // Never send email for a model that doesn't collect it
            const { email, ...rest } = leadData;
            const result = await onSubmit((showEmail ? { ...rest, email: email?.trim() } : rest) as LeadFormData);
            if (result === false || (typeof result === "object" && result?.success === false)) {
                if (onError) onError("Failed to submit. Please try again.");
            }
            // if parent handled submit successfully, clear local form
            if (result === true || (typeof result === "object" && result?.success === true)) {
                setLeadData({ name: "", phone: "", location: "", locationData: {} });
            }            
        } catch (err) {
            if (onError) onError("Something went wrong. Please try again later.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const isDisabled = isSubmitting || disabled || !consent.canSubmit || !!phoneError || !!nameError || !!locationError || (showEmail && !!emailError);

    return (
        <div ref={formRef} className={className}>            

            <CustomInput
                value={leadData.name}
                placeholder="Enter name"
                inputMode="text"
                autoFocus={!disableMobileAutofocus && isMobileLeadViewport() && !leadData.name.trim()}
                leftIcon={<ImageWrapper src="/user.svg" alt="user" title="user" width={18} height={18} cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL} />}
                onChange={handleNameChange}
                error={nameError || undefined}
                className="bg-gray-50"
                trackingClassName={MODAL_TRACKING.USER_NAME}
                testinputId="user-name"
            />

            <CustomInput
                type="tel"
                inputMode="tel"
                value={leadData.phone}
                placeholder="Enter mobile number"
                maxLength={10}
                autoFocus={!disableMobileAutofocus && isMobileLeadViewport() && !!leadData.name.trim() && !leadData.phone.trim()}
                leftIcon={<ImageWrapper src="/phone.svg" alt="phone" title="phone" width={18} height={18} cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL} />}
                onChange={handlePhoneChange}
                error={phoneError || undefined}
                className="bg-gray-50"
                trackingClassName={MODAL_TRACKING.USER_PHONE}
                testinputId="mobile-number"
            />

            {showEmail && (
                <CustomInput
                    type="email"
                    value={leadData.email || ""}
                    placeholder="Enter email address"
                    maxLength={254}
                    leftIcon={
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#90a1b9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="2" y="4" width="20" height="16" rx="2" />
                            <path d="m22 7-10 6L2 7" />
                        </svg>
                    }
                    onChange={handleEmailChange}
                    error={emailError || undefined}
                    className="bg-gray-50"
                    testinputId="email"
                />
            )}

            <label className="block mb-4 w-full ">
                <LocationMasterSearchInput value={leadData.location || ""} onChange={handleLocationChange} />
            </label>

            <LeadConsent
                consent={consent}
                className="mt-2"
            />

            <button
                type="button"
                onClick={handleSubmit}
                disabled={isDisabled}
                className={`w-full bg-[#008C8B] text-white py-3 rounded-md mt-4 focus:outline-none focus:ring-2 focus:ring-green-500 ${isDisabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"} ${LeadPopupSubmitTracking} ${MODAL_TRACKING.MODAL_LEAD_SUBMIT_CTA} ${leadsumbittracking}`}
                data-testid="car-lead-form-submit"
            >
                {isSubmitting ? "Submitting..." : submitLabel}
            </button>
        </div>
    );
};

export default LeadForm;
